import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import {
  applyPlaceholderPreview,
  BOOKING_PLACEHOLDERS,
  INVOICE_PLACEHOLDERS,
  type PlaceholderDoc,
} from "@/lib/templatePlaceholders";
import { canWriteStammdaten } from "@/lib/permissions";
import { useMailPlacePreview } from "@/lib/useMailPlacePreview";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";

interface TenantSettings {
  templates: {
    invoice_footer: { footer: string | null };
    booking_confirmation: {
      intro: string | null;
      cancellation_terms: string | null;
      footer: string | null;
    };
  };
}

interface TextSuggestions {
  invoice_footer: { footer?: string | null };
  booking_confirmation: {
    intro?: string | null;
    cancellation_terms?: string | null;
    footer?: string | null;
  };
}

type PdfTab = "invoice" | "booking";

function PreviewPanel({ text, label }: { text: string; label: string }) {
  const place = useMailPlacePreview();
  const preview = applyPlaceholderPreview(text, place.samples);
  if (!text.trim()) {
    return <p className="text-sm text-label-tertiary italic">Vorschau erscheint, sobald Sie Text eingeben.</p>;
  }
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-label-secondary">{label}</p>
      <div className="whitespace-pre-wrap rounded-lg border border-separator bg-fill/50 p-3 text-sm text-label">
        {preview}
      </div>
    </div>
  );
}

function PlaceholderList({ items }: { items: PlaceholderDoc[] }) {
  return (
    <ul className="space-y-1 text-xs text-label-secondary">
      {items.map((item) => (
        <li key={item.key}>
          <code className="rounded bg-fill px-1 py-0.5 text-label">{`{{${item.key}}}`}</code>
          {" — "}
          {item.description}
        </li>
      ))}
    </ul>
  );
}

export function TextTemplatesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [tab, setTab] = useState<PdfTab>("invoice");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invoiceFooter, setInvoiceFooter] = useState("");
  const [bookingIntro, setBookingIntro] = useState("");
  const [bookingCancellation, setBookingCancellation] = useState("");
  const [bookingFooter, setBookingFooter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        const templates = settings.templates;
        setInvoiceFooter(templates.invoice_footer.footer ?? "");
        setBookingIntro(templates.booking_confirmation.intro ?? "");
        setBookingCancellation(templates.booking_confirmation.cancellation_terms ?? "");
        setBookingFooter(templates.booking_confirmation.footer ?? "");
      })
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "PDF-Texte" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  async function loadSuggestion() {
    const suggestions = await api.get<TextSuggestions>("/api/v1/settings/templates/suggestions");
    if (tab === "invoice" && suggestions.invoice_footer.footer) {
      setInvoiceFooter(suggestions.invoice_footer.footer);
    }
    if (tab === "booking") {
      if (suggestions.booking_confirmation.intro) setBookingIntro(suggestions.booking_confirmation.intro);
      if (suggestions.booking_confirmation.cancellation_terms) {
        setBookingCancellation(suggestions.booking_confirmation.cancellation_terms);
      }
      if (suggestions.booking_confirmation.footer) setBookingFooter(suggestions.booking_confirmation.footer);
    }
  }

  async function save() {
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body =
        tab === "invoice"
          ? { invoice_footer: { footer: invoiceFooter.trim() || null } }
          : {
              booking_confirmation: {
                intro: bookingIntro.trim() || null,
                cancellation_terms: bookingCancellation.trim() || null,
                footer: bookingFooter.trim() || null,
              },
            };
      await api.patch("/api/v1/settings/templates", body);
      setSuccess(true);
      load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {!canWrite ? <StammdatenWriteHint /> : null}
      <Card className="space-y-2 p-4 sm:p-6">
        <p className="text-sm text-label-secondary">
          Hier liegen die Texte für PDF-Dokumente. Alle Mails, die verschickt werden, bearbeiten Sie
          unter E-Mail, inklusive Vorschau und HTML-Design.
        </p>
        <Link to="/mail/rundmail?bereich=vorlagen" className="text-sm font-medium text-accent hover:underline">
          E-Mails bearbeiten
        </Link>
      </Card>
      <div className="flex flex-wrap gap-1 rounded-xl border border-separator bg-grouped p-1">
        {(
          [
            ["invoice", "Rechnungs-PDF"],
            ["booking", "Buchungsbestätigung-PDF"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              setTab(id);
              setSuccess(false);
            }}
            className={
              tab === id
                ? "rounded-lg bg-accent px-3 py-2 text-sm font-medium text-label-on-accent shadow-sm"
                : "rounded-lg px-3 py-2 text-sm font-medium text-label-secondary hover:bg-fill hover:text-label"
            }
          >
            {label}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Card className="space-y-4 p-4 sm:p-6">
          {tab === "invoice" ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-label">Rechnungsfuß</h4>
                <Button type="button" variant="secondary" size="sm" disabled={!canWrite} onClick={() => void loadSuggestion()}>
                  Vorschlag laden
                </Button>
              </div>
              <textarea
                className="min-h-[120px] w-full rounded-lg border border-separator bg-background px-3 py-2 text-sm"
                value={invoiceFooter}
                onChange={(event) => setInvoiceFooter(event.target.value)}
                disabled={!canWrite}
              />
              <PreviewPanel text={invoiceFooter} label="Vorschau Rechnungsfuß" />
              <PlaceholderList items={INVOICE_PLACEHOLDERS} />
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-label">Einleitung, Storno und Fußzeile</h4>
                <Button type="button" variant="secondary" size="sm" disabled={!canWrite} onClick={() => void loadSuggestion()}>
                  Vorschlag laden
                </Button>
              </div>
              <label className="block text-sm font-medium text-label">Einleitung</label>
              <textarea
                className="min-h-[80px] w-full rounded-lg border border-separator bg-background px-3 py-2 text-sm"
                value={bookingIntro}
                onChange={(event) => setBookingIntro(event.target.value)}
                disabled={!canWrite}
              />
              <PreviewPanel text={bookingIntro} label="Vorschau Einleitung" />
              <label className="block text-sm font-medium text-label">Stornobedingungen</label>
              <textarea
                className="min-h-[100px] w-full rounded-lg border border-separator bg-background px-3 py-2 text-sm"
                value={bookingCancellation}
                onChange={(event) => setBookingCancellation(event.target.value)}
                disabled={!canWrite}
              />
              <PreviewPanel text={bookingCancellation} label="Vorschau Storno" />
              <label className="block text-sm font-medium text-label">Fußzeile</label>
              <textarea
                className="min-h-[80px] w-full rounded-lg border border-separator bg-background px-3 py-2 text-sm"
                value={bookingFooter}
                onChange={(event) => setBookingFooter(event.target.value)}
                disabled={!canWrite}
              />
              <PreviewPanel text={bookingFooter} label="Vorschau Fußzeile" />
              <PlaceholderList items={BOOKING_PLACEHOLDERS} />
            </>
          )}
          {canWrite ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={() => void save()} disabled={saving}>
                {saving ? "Speichern…" : "Speichern"}
              </Button>
              {success ? <span className="text-sm text-status-green">Gespeichert.</span> : null}
              {error ? <span className="text-sm text-status-red">{error}</span> : null}
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
